import { useEffect } from 'react';
import { Video as LucideIcon } from 'lucide-react';
import { motion, animate, useMotionValue, useTransform, useReducedMotion } from 'framer-motion';

interface KpiCardProps {
  label: string;
  count: number;
  icon: LucideIcon;
  colorClass: string;
  loading?: boolean;
  onClick?: () => void;
  formatValue?: (value: number) => string;
  sublabel?: string;
}

function AnimatedCount({ value, format }: { value: number; format?: (value: number) => string }) {
  const reducedMotion = useReducedMotion();
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, v => {
    const n = Math.round(v);
    return format ? format(n) : n.toLocaleString('pt-PT');
  });

  useEffect(() => {
    if (reducedMotion) {
      motionValue.set(value);
      return;
    }
    const controls = animate(motionValue, value, { duration: 0.9, ease: [0.16, 1, 0.3, 1] });
    return () => controls.stop();
  }, [value, reducedMotion, motionValue]);

  return <motion.span>{rounded}</motion.span>;
}

export function KpiCard({ label, count, icon: Icon, colorClass, loading, onClick, formatValue, sublabel }: KpiCardProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-card p-5 shadow-card">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-gray-100 rounded-full"></div>
          </div>
          <div className="h-7 bg-gray-100 rounded-full w-14 mb-1.5"></div>
          <div className="h-3 bg-gray-100 rounded-full w-20"></div>
        </div>
      </div>
    );
  }

  // The hero card (Pagamentos Prontos) is the ink card with a lime figure,
  // like the Content Hub's dark cards; the rest are paper-white 24px cards.
  const isHero = colorClass.includes('hero-emerald');
  const accent = isHero
    ? { card: 'bg-brand-ink', iconBg: 'bg-brand-lime/15', iconText: 'text-brand-lime', numText: 'text-brand-lime', labelText: 'text-white/70', subText: 'text-white/50' }
    : colorClass.includes('red')
    ? { card: 'bg-white', iconBg: 'bg-red-50', iconText: 'text-red-600', numText: 'text-brand-ink', labelText: 'text-gray-500', subText: 'text-gray-400' }
    : colorClass.includes('orange')
    ? { card: 'bg-white', iconBg: 'bg-orange-50', iconText: 'text-orange-600', numText: 'text-brand-ink', labelText: 'text-gray-500', subText: 'text-gray-400' }
    : { card: 'bg-white', iconBg: 'bg-brand-tint', iconText: 'text-brand-ink', numText: 'text-brand-ink', labelText: 'text-gray-500', subText: 'text-gray-400' };

  return (
    <motion.div
      className={`group ${accent.card} shadow-card rounded-card p-5 ${
        onClick ? 'cursor-pointer' : 'cursor-default'
      }`}
      onClick={onClick}
      whileHover={onClick ? { y: -3, boxShadow: '0 8px 24px rgba(21, 20, 15, 0.12)' } : undefined}
      whileTap={onClick ? { scale: 0.97 } : undefined}
      transition={{ type: 'spring', stiffness: 400, damping: 28 }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-full ${accent.iconBg} transition-colors group-hover:scale-110 transition-transform duration-300`}>
          <Icon className={`w-4 h-4 ${accent.iconText}`} />
        </div>
      </div>
      <p className={`text-2xl font-bold mb-0.5 leading-none tracking-tight tabular-nums ${accent.numText}`}>
        <AnimatedCount value={count} format={formatValue} />
      </p>
      <p className={`text-xs font-medium leading-tight ${accent.labelText}`}>
        {label}
      </p>
      {sublabel && (
        <p className={`text-[11px] leading-tight mt-0.5 ${accent.subText}`}>
          {sublabel}
        </p>
      )}
    </motion.div>
  );
}
